import React, { useState, useEffect } from "react";
import {
  Button, Form, Modal, Spinner, Card, Badge, Alert
} from 'react-bootstrap';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IMAGE_TAGS } from "../../../managers/ContentManager";
import TagSelector from "./TagSelector";
import useProductApi, { Product } from '../../../managers/ProductManager';
import { TEMPLATE_GROUPS } from '../../../managers/TemplateGroupManager';
import { User, UserRoles } from '../../../managers/UserManager';
import Select from "react-select";
import { FaPlus, FaTrash } from "react-icons/fa";

interface ProductEditorProps {
  product: Product;
  currentUser: User;
  onSave: (updatedProduct: Product) => void;
  onClose: () => void;
  show?: boolean;
  onExited?: () => void;
}

const STATUS_TAG_OPTIONS = [
  { value: "PRIVATE", label: "Private" },
  { value: "PUBLIC", label: "Public" },
  { value: "FEATURED", label: "Featured" },
  { value: "TEMPLATE", label: "Template" }
];

const ProductEditor: React.FC<ProductEditorProps> = ({ product, currentUser, onSave, onClose, show = true, onExited }) => {
  const { updateProduct } = useProductApi();
  const [formState, setFormState] = useState<Product>(new Product());
  const [saving, setSaving] = useState(false);

  // Check if user can create templates
  const canCreateTemplate = currentUser.roles?.includes(UserRoles.CREATOR) || currentUser.roles?.includes(UserRoles.SUPER_ADMIN);

  // Filter status options based on user role
  const availableStatusOptions = canCreateTemplate
    ? STATUS_TAG_OPTIONS
    : STATUS_TAG_OPTIONS.filter(option => option.value !== 'TEMPLATE');

  useEffect(() => {
    setFormState(new Product(product));
  }, [product]);

  const handleChange = (field: keyof Product, value: any) => {
    let sanitizedValue = value;

    if (field === "tags" && Array.isArray(value)) {
      // For templates, validate against TEMPLATE_GROUPS; for regular products, validate against IMAGE_TAGS
      if (formState.statusTag === 'TEMPLATE') {
        const validTemplateGroupSet = new Set(TEMPLATE_GROUPS.map(g => g.id));
        sanitizedValue = value.filter((tag) => validTemplateGroupSet.has(tag));
      } else {
        const validTagSet = new Set(IMAGE_TAGS.map(t => t.tag));
        sanitizedValue = value.filter((tag) => validTagSet.has(tag));
      }
    }

    // Handle Template status tag selection
    if (field === "statusTag") {
      if (value === 'TEMPLATE' && !formState.templateConfig) {
        // Initialize template config when Template is selected
        const updated = new Product({
          ...formState,
          statusTag: 'TEMPLATE',
          templateConfig: {
            parameters: [],
            promptTemplate: formState.prompt || ''
          }
        });
        setFormState(updated);
        return;
      } else if (value !== 'TEMPLATE' && formState.templateConfig) {
        // Remove template config when switching away from Template
        const updated = new Product({ ...formState, statusTag: sanitizedValue, templateConfig: undefined });
        setFormState(updated);
        return;
      }
    }

    const updated = new Product({ ...formState, [field]: sanitizedValue });
    setFormState(updated);
  };

  // Template configuration helpers
  const addParameter = () => {
    const newParam = {
      id: `param_${Date.now()}`,
      label: 'New Parameter',
      type: 'text' as const,
      placeholder: '',
      required: false
    };

    const updated = new Product({
      ...formState,
      templateConfig: {
        ...formState.templateConfig!,
        parameters: [...(formState.templateConfig?.parameters || []), newParam]
      }
    });
    setFormState(updated);
  };

  const updateParameter = (index: number, field: string, value: any) => {
    const parameters = [...(formState.templateConfig?.parameters || [])];
    parameters[index] = { ...parameters[index], [field]: value };

    const updated = new Product({
      ...formState,
      templateConfig: {
        ...formState.templateConfig!,
        parameters
      }
    });
    setFormState(updated);
  };

  const removeParameter = (index: number) => {
    const parameters = formState.templateConfig?.parameters.filter((_, i) => i !== index) || [];

    const updated = new Product({
      ...formState,
      templateConfig: {
        ...formState.templateConfig!,
        parameters
      }
    });
    setFormState(updated);
  };

  const updatePromptTemplate = (value: string) => {
    const updated = new Product({
      ...formState,
      templateConfig: {
        ...formState.templateConfig!,
        promptTemplate: value
      }
    });
    setFormState(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateProduct(formState);
      toast.success("Product updated successfully.");
      onSave(updated);
    } catch (err) {
      console.error("Failed to update product:", err);
      toast.error("Failed to update product.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} onExited={onExited} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Product</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label htmlFor="name">Name</Form.Label>
            <Form.Control
              id="name"
              value={formState.name || formState.title || ''}
              onChange={e => handleChange('name', e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="prompt">Prompt</Form.Label>
            <Form.Control
              as="textarea"
              id="prompt"
              value={formState.prompt || ''}
              onChange={e => handleChange('prompt', e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            {formState.statusTag === 'TEMPLATE' ? (
              // Template group selector (single select)
              <>
                <Form.Label>Template Group</Form.Label>
                <Form.Select
                  value={formState.tags?.[0] || ''}
                  onChange={(e) => {
                    const newTags = e.target.value ? [e.target.value] : [];
                    handleChange('tags', newTags);
                  }}
                >
                  <option value="">Select a group...</option>
                  {TEMPLATE_GROUPS.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose the primary category for this template
                </Form.Text>
              </>
            ) : (
              // Regular product tag selector (multi-select)
              <>
                <Form.Label>Tags</Form.Label>
                <TagSelector
                  selectedTags={formState.tags || []}
                  onChange={(updated) => handleChange("tags", updated)}
                />
              </>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Select
              required={true}
              options={availableStatusOptions}
              value={
                availableStatusOptions.find(option => option.value === formState.statusTag) || null
              }
              onChange={(selectedOption) =>
                handleChange('statusTag', selectedOption?.value || '')
              }
              placeholder="Select visibility and feature status..."
              className="mb-3"
              isClearable
            />
            {!canCreateTemplate && (
              <Form.Text className="text-muted">
                Only creators and super admins can create templates.
              </Form.Text>
            )}
          </Form.Group>

          {/* Template Configuration Section */}
          {formState.statusTag === 'TEMPLATE' && (
            <>
              <hr className="my-4" />
              <Card className="mb-3">
                <Card.Header>
                  <strong>Template Configuration</strong>
                </Card.Header>
                <Card.Body>
                  {/* Prompt Template */}
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Prompt Template
                      <Badge bg="secondary" className="ms-2">Use {`{{paramId}}`} for placeholders</Badge>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={formState.templateConfig?.promptTemplate || ''}
                      onChange={(e) => updatePromptTemplate(e.target.value)}
                      placeholder="e.g., Action figure for {{major}} student with {{items}}"
                    />
                    <Form.Text className="text-muted">
                      Placeholders like {`{{major}}`} will be replaced with user-provided values
                    </Form.Text>
                  </Form.Group>

                  {/* Parameters List */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">Parameters</Form.Label>
                      <Button variant="outline-primary" size="sm" onClick={addParameter}>
                        <FaPlus /> Add Parameter
                      </Button>
                    </div>

                    {formState.templateConfig?.parameters && formState.templateConfig.parameters.length > 0 ? (
                      formState.templateConfig.parameters.map((param, index) => (
                        <Card key={index} className="mb-2">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <Badge bg="secondary">{`{{${param.id}}}`}</Badge>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeParameter(index)}
                              >
                                <FaTrash />
                              </Button>
                            </div>

                            <Form.Group className="mb-2">
                              <Form.Label>Parameter ID</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={param.id}
                                onChange={(e) => updateParameter(index, 'id', e.target.value)}
                                placeholder="e.g., major, photo, items"
                              />
                            </Form.Group>

                            <Form.Group className="mb-2">
                              <Form.Label>Label</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={param.label}
                                onChange={(e) => updateParameter(index, 'label', e.target.value)}
                                placeholder="e.g., Your Major"
                              />
                            </Form.Group>

                            <Form.Group className="mb-2">
                              <Form.Label>Type</Form.Label>
                              <Form.Select
                                size="sm"
                                value={param.type}
                                onChange={(e) => updateParameter(index, 'type', e.target.value)}
                              >
                                <option value="text">Text</option>
                                <option value="textarea">Textarea</option>
                                <option value="image">Image Upload</option>
                              </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-2">
                              <Form.Label>Placeholder</Form.Label>
                              <Form.Control
                                type="text"
                                size="sm"
                                value={param.placeholder || ''}
                                onChange={(e) => updateParameter(index, 'placeholder', e.target.value)}
                                placeholder="Optional hint text"
                              />
                            </Form.Group>

                            <Form.Check
                              type="checkbox"
                              label="Required"
                              checked={param.required}
                              onChange={(e) => updateParameter(index, 'required', e.target.checked)}
                            />
                          </Card.Body>
                        </Card>
                      ))
                    ) : (
                      <Alert variant="info">
                        No parameters defined. Click "Add Parameter" to create template fields.
                      </Alert>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" className="d-flex align-items-center gap-2" disabled={saving}>
              {saving && <Spinner size="sm" />} {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ProductEditor;
